import Component from '@bythepixel/component-loader';
import breakpoints from '../../constants/breakpoints';

/**
 * Configuration for scroll grow behavior
 * Adjust these values to modify the animation characteristics
 */
const CONFIG = {
  /**
   * Start position of expansion (0-1 range)
   * 0 = bottom of viewport, 1 = top of viewport
   * Default: 0 (starts when element top reaches bottom of viewport)
   */
  startPosition: 0,

  /**
   * End position of expansion (0-1 range)
   * 0 = bottom of viewport, 1 = top of viewport
   * Default: 0.5 (fully expanded when element top reaches middle of viewport)
   */
  endPosition: 0.5,

  /**
   * Root margin for Intersection Observer
   * Format: 'top right bottom left' (like CSS margin)
   * Larger values = start observing earlier (before element enters viewport)
   * Default: '100% 0px 100% 0px' (start watching 100% of viewport height before/after)
   */
  observerRootMargin: '100% 0px 100% 0px',

  /**
   * Intersection Observer threshold values
   * Array of ratios (0-1) at which to trigger the callback
   * Default: [0, 1] (trigger at 0% and 100% visibility)
   */
  observerThreshold: [0, 1],

  /**
   * Initial border radius in pixels (animates to 0)
   */
  initialBorderRadius: 6,

  /**
   * Minimum screen width to apply fixed positioning (prevents mobile overflow)
   */
  fixedPositioningBreakpoint: breakpoints['break-2'], // 768px (md breakpoint)
};

export default class ScrollGrowContainer extends Component {
  static selector = '[data-comp-scroll-grow-container]';

  #observer = null;
  #ticking = false;
  #isActive = false;
  #paddingCache = { left: 0, right: 0 };
  #maxWidth = null;
  #boundScrollHandler = null;
  #boundResizeHandler = null;
  #config = CONFIG;
  #overlayContent = null;
  #initialOverlayOffset = { left: 0, bottom: 0 };

  constructor(...args) {
    super(...args);
    this.cacheInitialStyles();
    this.cacheOverlayElement();
    this.setupIntersectionObserver();
    this.events();
  }

  /**
   * Cache the overlay content element and its initial offset
   */
  cacheOverlayElement() {
    // Find the overlay content element
    const overlay = this.$container.querySelector('.promotional-video__overlay-content');
    if (overlay) {
      this.#overlayContent = overlay;
      // Get computed left position (handles both px and percentage values)
      const styles = window.getComputedStyle(overlay);
      const computedLeft = parseInt(styles.left, 10) || 0;
      const padding = parseInt(styles.paddingLeft, 10) || 0;
      this.#initialOverlayOffset.left = computedLeft;
      this.#initialOverlayOffset.bottom = padding;
    }
  }

  /**
   * Check if we should apply fixed positioning (only on larger screens)
   */
  static shouldApplyFixedPositioning() {
    return window.innerWidth >= CONFIG.fixedPositioningBreakpoint;
  }

  /**
   * Cache the initial padding values and max-width from computed styles
   */
  cacheInitialStyles() {
    const styles = window.getComputedStyle(this.$container);
    this.#paddingCache.left = parseInt(styles.paddingLeft, 10) || 0;
    this.#paddingCache.right = parseInt(styles.paddingRight, 10) || 0;

    // Get the actual max-width value (not 'none')
    const { maxWidth } = styles;
    this.#maxWidth = maxWidth !== 'none' ? parseInt(maxWidth, 10) : null;
  }

  /**
   * Calculate scroll progress (0 = not expanded, 1 = fully expanded)
   * Based on element's position in viewport
   */
  calculateProgress() {
    const rect = this.$container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate position (0 = bottom of viewport, 1 = top of viewport)
    const position = (viewportHeight - rect.top) / viewportHeight;

    // Map position to progress based on config start/end positions
    const { startPosition, endPosition } = this.#config;
    const range = endPosition - startPosition;

    if (range === 0) return position >= startPosition ? 1 : 0;

    const progress = (position - startPosition) / range;

    // Clamp between 0 and 1
    return Math.max(0, Math.min(1, progress));
  }

  /**
   * Apply the expansion effect based on scroll progress
   */
  applyExpansion(progress) {
    // Interpolate padding from original values to 0
    const paddingLeft = this.#paddingCache.left * (1 - progress);
    const paddingRight = this.#paddingCache.right * (1 - progress);

    this.$container.style.paddingLeft = `${paddingLeft}px`;
    this.$container.style.paddingRight = `${paddingRight}px`;

    // Interpolate border-radius from initial value to 0
    const borderRadius = this.#config.initialBorderRadius * (1 - progress);
    this.$container.style.borderRadius = `${borderRadius}px`;

    // Content is now centered and styled to match fluid-container
    // No need to manipulate left position - it stays centered
    if (this.#overlayContent && !ScrollGrowContainer.shouldApplyFixedPositioning()) {
      // On mobile, reset to default positioning
      this.#overlayContent.style.left = '';
    }

    // If there's a max-width, interpolate it to a very large value (effectively removing it)
    if (this.#maxWidth !== null) {
      const totalPadding = this.#paddingCache.left + this.#paddingCache.right;
      const viewportExpansion = (window.innerWidth - this.#maxWidth) * progress;
      const fullExpansion = this.#maxWidth + totalPadding + viewportExpansion;
      this.$container.style.maxWidth = `${fullExpansion}px`;
    }
  }

  /**
   * Handle scroll events with requestAnimationFrame throttling
   */
  handleScroll() {
    if (!this.#ticking) {
      requestAnimationFrame(() => {
        const progress = this.calculateProgress();
        this.applyExpansion(progress);
        this.#ticking = false;
      });
      this.#ticking = true;
    }
  }

  /**
   * Set up Intersection Observer to manage scroll listener
   */
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) {
      // Fallback: just apply the effect once if IntersectionObserver isn't supported
      this.applyExpansion(1);
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.activateScrollListener();
        } else {
          this.deactivateScrollListener();
        }
      },
      {
        rootMargin: this.#config.observerRootMargin,
        threshold: this.#config.observerThreshold,
      },
    );

    this.#observer.observe(this.$container);
  }

  /**
   * Activate scroll listener when element is near viewport
   */
  activateScrollListener() {
    if (this.#isActive) return;

    this.#isActive = true;
    this.#boundScrollHandler = () => this.handleScroll();
    window.addEventListener('scroll', this.#boundScrollHandler, { passive: true });

    // Calculate initial state
    this.handleScroll();
  }

  /**
   * Deactivate scroll listener when element is far from viewport
   */
  deactivateScrollListener() {
    if (!this.#isActive) return;

    this.#isActive = false;
    if (this.#boundScrollHandler) {
      window.removeEventListener('scroll', this.#boundScrollHandler);
      this.#boundScrollHandler = null;
    }
  }

  /**
   * Handle window resize to recalculate padding and max-width values
   */
  handleResize() {
    if (!this.#ticking) {
      requestAnimationFrame(() => {
        // Reset styles to get accurate measurements
        this.$container.style.paddingLeft = '';
        this.$container.style.paddingRight = '';
        this.$container.style.maxWidth = '';
        if (this.#overlayContent) {
          this.#overlayContent.style.left = '';
        }

        this.cacheInitialStyles();
        this.cacheOverlayElement();

        if (this.#isActive) {
          const progress = this.calculateProgress();
          this.applyExpansion(progress);
        }
        this.#ticking = false;
      });
      this.#ticking = true;
    }
  }

  events() {
    this.#boundResizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.#boundResizeHandler);
  }

  /**
   * Cleanup when component is destroyed
   */
  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    this.deactivateScrollListener();

    if (this.#boundResizeHandler) {
      window.removeEventListener('resize', this.#boundResizeHandler);
      this.#boundResizeHandler = null;
    }
  }
}
